import React, { useCallback, useContext, useMemo } from 'react';
import { cn } from '@sglara/cn';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import CloseModal from 'design-system/src/assets/icons/closeModal.svg?react';
import Button from 'design-system/src/components/Button/Button';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { AuthContext } from '../../../auth';
import { client, OrgMember } from '../../../api';
import type { OnboardingInfoResponse } from '../../../api';
import { SUPPORTED_EHR_NAMES } from '../../../types';
import { getEhrDisplayName } from '../../../utils';
import { ScheduleTrainingWidget } from '../ScheduleTrainingWidget';
import { useDismissGetStartedPanel } from '../../../hooks/useDismissGetStartedPanel';
import { OnboardingSidePanelCard } from './OnboardingSidePanelCard';
import { ONBOARDING_STEP_DEFINITIONS, OnboardingStepModalKey } from '../onboardingStepDefinitions';
import { createTrackingProps } from 'shared-utils/analytics';

export type GetStartedPanelProps = {
  /**
   * Whether the panel is open
   */
  isOpen: boolean;
  /**
   * Callback when the panel should close
   */
  onClose: () => void;
  /**
   * Class name for additional styling
   */
  className?: string;
};

/**
 * A slide-out panel that appears to the right of the navigation sidebar.
 * Uses Headless UI Dialog for accessibility (focus trap, Escape key, click-outside-to-close, ARIA).
 */
export const GetStartedPanel = ({ isOpen, onClose, className }: GetStartedPanelProps) => {
  const authContext = useContext(AuthContext);
  const isAdmin = authContext.user?.is_admin || false;

  const dismissPanel = useDismissGetStartedPanel();

  const memberQuery = useQuery<{ member: OrgMember }>({
    queryKey: ['orgMember', authContext.user?.email],
    queryFn: () => client.get('org-member/current-user').then((res) => res.data),
    enabled: isOpen,
  });

  const onboardingInfoQuery = useQuery<OnboardingInfoResponse>({
    queryKey: ['onboardingInfo'],
    queryFn: () => client.get('onboarding/info').then((res) => res.data),
    enabled: isOpen,
  });

  const { data: ehrData } = useQuery({
    queryKey: ['ehrName'],
    queryFn: () => client.get('/onboarding/ehr-name').then((res) => res.data),
    enabled: isOpen,
  });
  const supportedEhr = ehrData?.ehr && SUPPORTED_EHR_NAMES.includes(ehrData.ehr) ? ehrData.ehr : null;

  const practiceStatuses = onboardingInfoQuery.data?.practice_statuses;

  const visibleSteps = useMemo(() => {
    if (!practiceStatuses) return [];
    return ONBOARDING_STEP_DEFINITIONS.filter((step) => {
      if (step.adminOnly && !isAdmin) return false;
      if (step.key === OnboardingStepModalKey.EhrIntegration) {
        return !!supportedEhr && practiceStatuses.ehr_integration_status === 'awaiting_credentials';
      }
      return true;
    });
  }, [practiceStatuses, isAdmin, supportedEhr]);

  const getStepCompletionStatus = useCallback(
    (stepKey: OnboardingStepModalKey): boolean => {
      if (!practiceStatuses) return false;
      switch (stepKey) {
        case OnboardingStepModalKey.AddUsers:
          return practiceStatuses.has_invited_teammates;
        case OnboardingStepModalKey.AddProviders:
          return practiceStatuses.has_added_providers;
        case OnboardingStepModalKey.AddPhoneNumbers:
          return practiceStatuses.has_added_phone_numbers;
        case OnboardingStepModalKey.EhrIntegration:
          return practiceStatuses.has_ehr_integration;
        case OnboardingStepModalKey.AddOffices:
          return practiceStatuses.has_added_office;
        case OnboardingStepModalKey.SendFirstPrescription:
          return practiceStatuses.has_queued_prescriptions;
        case OnboardingStepModalKey.NotificationPreferences:
          return practiceStatuses.has_saved_notification_preferences;
        default:
          return false;
      }
    },
    [practiceStatuses],
  );

  const { completedSteps, uncompletedSteps } = useMemo(() => {
    const completed = visibleSteps.filter((step) => getStepCompletionStatus(step.key));
    const uncompleted = visibleSteps.filter((step) => !getStepCompletionStatus(step.key));
    return { completedSteps: completed, uncompletedSteps: uncompleted };
  }, [visibleSteps, getStepCompletionStatus]);

  const ehrDisplayName = supportedEhr ? getEhrDisplayName(supportedEhr) : '';

  const isLoading = memberQuery.isLoading || onboardingInfoQuery.isLoading;

  const getWelcomeString = (): string => {
    if (memberQuery.data?.member.name) {
      const firstName = memberQuery.data.member.name.split(' ')[0];
      return `Welcome, ${firstName}!`;
    }
    return 'Welcome!';
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className={cn('relative z-10', className)}>
      <DialogBackdrop
        transition
        className="fixed inset-y-0 left-[var(--nav-sidebar-width)] right-0 bg-gray-overlay-backdrop backdrop-blur-md transition-opacity duration-300 ease-out data-[closed]:opacity-0"
      />

      <DialogPanel
        transition
        className="fixed inset-y-0 left-[var(--nav-sidebar-width)] flex w-[568px] flex-col bg-white px-4 shadow-[12px_0_12px_-4px_rgba(0,0,0,0.1)] transition-transform duration-300 ease-out data-[closed]:-translate-x-full"
      >
        {isLoading ? (
          <LoadingSpinner height="100%" size="24px" />
        ) : (
          <>
            <div className="flex items-start justify-between pt-6">
              <div className="flex flex-col gap-1">
                <Typography styledAs="h6" renderedAs="h2">
                  {getWelcomeString()}
                </Typography>
                <Typography styledAs="bodyLargeDMSans" color="var(--grayscale-600)">
                  Below are suggestions on ways to get started
                </Typography>
              </div>
              <button
                onClick={onClose}
                className="mt-1 flex shrink-0 cursor-pointer items-center justify-center rounded-medium border-none bg-transparent p-1 transition-colors text-body-small-space-grotesk focus-ring hover:bg-grayscale-50"
                aria-label="Close panel"
                {...createTrackingProps({ elementName: 'Get Started Panel: Close' })}
              >
                <CloseModal width={20} height={20} aria-hidden />
              </button>
            </div>

            <hr className="my-6 h-px w-full border-none bg-grayscale-100" />

            <div className="flex flex-1 flex-col gap-3 overflow-y-auto pb-3">
              {onboardingInfoQuery.data &&
                onboardingInfoQuery.data.practice_statuses.training_session_status === 'awaiting_booking' && (
                  <div className="flex h-[298px] flex-col items-center justify-center gap-4 self-stretch rounded-lg border border-solid border-grayscale-100 bg-surface-100 p-4">
                    <ScheduleTrainingWidget
                      trainingSessionStatus={onboardingInfoQuery.data.practice_statuses.training_session_status}
                    />
                  </div>
                )}

              {onboardingInfoQuery.data &&
                onboardingInfoQuery.data.practice_statuses.training_session_status === 'booked' && (
                  <ScheduleTrainingWidget
                    trainingSessionStatus={onboardingInfoQuery.data.practice_statuses.training_session_status}
                    variant="compact"
                  />
                )}

              {uncompletedSteps.map((step) => (
                <OnboardingSidePanelCard
                  key={step.key}
                  icon={step.icon}
                  title={step.title({ ehrDisplayName })}
                  description={step.description}
                  href={step.href}
                  isCompleted={false}
                />
              ))}

              {completedSteps.length > 0 && (
                <>
                  <Typography styledAs="h6" className="mt-6">
                    Completed
                  </Typography>
                  {completedSteps.map((step) => (
                    <OnboardingSidePanelCard
                      key={step.key}
                      icon={step.icon}
                      title={step.title({ ehrDisplayName })}
                      description={step.description}
                      href={step.href}
                      isCompleted={true}
                    />
                  ))}
                </>
              )}
            </div>

            <hr className="h-px w-full shrink-0 border-none bg-grayscale-100" />

            <div className="shrink-0 py-4">
              <Button
                variant="tertiary"
                size="sm"
                isLoading={dismissPanel.isPending}
                onClick={() => dismissPanel.mutate(undefined, { onSuccess: onClose })}
                {...createTrackingProps({ elementName: "Get Started Panel: Don't Show This Anymore" })}
              >
                Don't show this anymore
              </Button>
            </div>
          </>
        )}
      </DialogPanel>
    </Dialog>
  );
};
